// src/components/nanny/NannyCard.jsx
import { MapPin, Calendar, Shield, UserCheck, Phone, CheckCircle, XCircle } from 'lucide-react';
import Card from '../layout/ui/Card';
import Button from '../layout/ui/Button';
import RatingDisplay from '../common/RatingDisplay';
import RateUser from '../common/RateUser';

export default function NannyCard({ nanny, onViewDetails }) {
  const hasData = nanny.age || nanny.state_of_origin;

  const getAvailabilityBadge = () => {
    if (nanny.is_active === null || nanny.is_active === undefined) return null;

    return nanny.is_active ? (
      <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
        <CheckCircle size={16} />
        Available
      </span>
    ) : (
      <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
        <XCircle size={16} />
        Not Available
      </span>
    );
  };

  return (
    <Card className="p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="flex gap-6" onClick={() => onViewDetails?.(nanny)}>
        {/* Avatar */}
        <div className="w-28 h-28 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex-shrink-0 flex items-center justify-center border-2 border-dashed border-gray-300">
          <div className="text-5xl font-bold text-primary/70 rounded-full">
            { nanny.profile_picture && !nanny.profile_picture.includes('default-avatar') ? <img
                    src={process.env.REACT_APP_IMAGE_URL+nanny?.profile_picture}
                    alt={`${nanny.full_name}`}
                    className="w-full h-full object-cover rounded-full "
                  /> : nanny?.full_name?.[0]?.toUpperCase() || 'U'}
          </div>
        </div>

        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 truncate">{nanny?.full_name}</h3>
              <div className="flex items-center gap-3 mt-2">
                <RatingDisplay rating={nanny?.rating || 0} size="md" />
              </div>
              <p className="text-muted flex items-center gap-2 mt-2 text-base">
                <Phone size={16} /> {nanny?.phone_number}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
              {getAvailabilityBadge()}
              {nanny.is_vetted ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  <Shield size={16} />
                  Vetted
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  <UserCheck size={16} />
                  Pending Vetting
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm text-gray-600">
            {nanny.age && (
              <p className="flex items-center gap-2">
                <Calendar size={16} /> {nanny.age} years old
              </p>
            )}
            {nanny.state_of_origin && (
              <p className="flex items-center gap-2 truncate">
                <MapPin size={16} /> {nanny.state_of_origin.name || nanny.state_of_origin}
              </p>
            )}
            {nanny.work_experiences?.length > 0 && (
              <p>
                {nanny.work_experiences.length} past job{nanny.work_experiences.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {!hasData && (
            <p className="text-sm text-orange-600 mt-4 italic font-medium">
              Profile incomplete – vetting in progress
            </p>
          )}
        </div>
      </div>

      {/* Action Bar */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-200">
        <div 
          className="flex items-center gap-4"
          onClick={(e) => e.stopPropagation()} // ← THIS STOPS MODAL FROM OPENING ON RATING
        >
          <RateUser 
            userId={nanny?.profile?.id || nanny?.id} 
            initialRating={nanny?.user_rating}
            disabled={nanny?.has_rated}
            size="md"
          />
        </div>

        <Button 
          size="lg" 
          className="bg-primary hover:bg-primary/90 text-white font-bold px-8"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.(nanny);
          }}
        >
          View Profile & Make Offer
        </Button>
      </div>
    </Card>
  );
}